/*
 * Client-side wishlist.
 *
 * There is no native Shopify wishlist, and this store has no wishlist app, so
 * the list lives in localStorage on the shopper's own device. That means it is
 * per-browser: it does not follow a customer between devices and it is not
 * visible to the shop. Swapping in an app later means replacing the Wishlist
 * object below; the custom elements around it can stay.
 *
 * Each entry stores the handful of fields needed to render a card, captured at
 * the moment of saving. The wishlist page can then render with no network call
 * and no dependency on the product still being in a collection.
 */
const Wishlist = (() => {
  const KEY = 'pujasets:wishlist';

  // Private browsing and "block site data" make localStorage throw on access
  // rather than return null, so every call is guarded.
  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      // Quota or blocked storage: the page keeps working, the list just
      // does not persist.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', {detail: {count: list.length}}));
  }

  return {
    all: read,
    count: () => read().length,
    has: (handle) => read().some((i) => i.handle === handle),
    toggle(item) {
      const list = read();
      const at = list.findIndex((i) => i.handle === item.handle);
      if (at > -1) list.splice(at, 1);
      else list.push(item);
      write(list);
      return at === -1;
    },
    remove(handle) {
      write(read().filter((i) => i.handle !== handle));
    },
  };
})();

/* The heart on a product card. Carries the card's own data so the wishlist
   page can render without fetching anything. */
if (!customElements.get('wishlist-toggle')) {
  customElements.define(
    'wishlist-toggle',
    class WishlistToggle extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;
        this.button.addEventListener('click', this.onClick);
        document.addEventListener('wishlist:change', this.sync);
        this.sync();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        document.removeEventListener('wishlist:change', this.sync);
      }

      onClick = (event) => {
        // Cards wrap their whole body in a link, so the click must not
        // navigate to the product.
        event.preventDefault();
        event.stopPropagation();
        Wishlist.toggle({
          handle: this.dataset.handle,
          title: this.dataset.title,
          url: this.dataset.url,
          image: this.dataset.image,
          price: this.dataset.price,
        });
      };

      sync = () => {
        const saved = Wishlist.has(this.dataset.handle);
        this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        this.button.setAttribute(
          'aria-label',
          (saved ? this.dataset.labelRemove : this.dataset.labelAdd) || 'Wishlist'
        );
        this.classList.toggle('wishlist-toggle--on', saved);
      };
    }
  );
}

/* The count bubble in the header. */
if (!customElements.get('wishlist-count')) {
  customElements.define(
    'wishlist-count',
    class WishlistCount extends HTMLElement {
      connectedCallback() {
        document.addEventListener('wishlist:change', this.sync);
        // Another tab writing to the same key fires `storage`, not our own
        // custom event, so both are listened for.
        window.addEventListener('storage', this.sync);
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this.sync);
        window.removeEventListener('storage', this.sync);
      }

      sync = () => {
        const n = Wishlist.count();
        this.textContent = n > 0 ? String(n) : '';
        this.hidden = n === 0;
      };
    }
  );
}

/* The wishlist page body. */
if (!customElements.get('wishlist-list')) {
  customElements.define(
    'wishlist-list',
    class WishlistList extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.empty = this.querySelector('[data-wishlist-empty]');
        this.addEventListener('click', this.onClick);
        document.addEventListener('wishlist:change', this.render);
        window.addEventListener('storage', this.render);
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this.render);
        window.removeEventListener('storage', this.render);
      }

      onClick = (event) => {
        const remove = event.target.closest('[data-wishlist-remove]');
        if (!remove) return;
        event.preventDefault();
        Wishlist.remove(remove.dataset.wishlistRemove);
      };

      render = () => {
        const items = Wishlist.all();
        this.empty.hidden = items.length > 0;
        this.grid.hidden = items.length === 0;
        this.grid.replaceChildren(...items.map((i) => this.card(i)));
      };

      card(item) {
        // Built with DOM calls rather than innerHTML: every field here was
        // read out of the page and round-tripped through localStorage, which
        // a different script on the device could have edited.
        const li = document.createElement('li');
        li.className = 'wishlist-card';

        const a = document.createElement('a');
        a.href = item.url || '#';
        a.className = 'wishlist-card__link';

        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = item.title || '';
          img.loading = 'lazy';
          img.className = 'wishlist-card__image';
          a.appendChild(img);
        }

        const h = document.createElement('span');
        h.className = 'wishlist-card__title';
        h.textContent = item.title || '';
        a.appendChild(h);

        if (item.price) {
          const p = document.createElement('span');
          p.className = 'wishlist-card__price';
          p.textContent = item.price;
          a.appendChild(p);
        }

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wishlist-card__remove link';
        btn.dataset.wishlistRemove = item.handle;
        btn.textContent = this.dataset.removeLabel || 'Remove';

        li.append(a, btn);
        return li;
      }
    }
  );
}
